/**
 * Bootstrap Provider 组件
 * 在应用启动时调用 Bootstrap API 并将数据存入 RegionStore
 *
 * 功能：
 * - 应用启动时自动调用 Bootstrap API
 * - 将返回的区域配置存入 RegionStore
 * - 处理加载和错误状态
 * - 支持 locale 参数
 *
 * **Validates: Requirements 1.1**
 */

import React, { useEffect } from "react";
import { useBootstrap } from "@/hooks/useBootstrap";
import { useRegionStore } from "@/stores/regionStore";
import i18n from "@/config/i18n";

interface BootstrapProviderProps {
  children: React.ReactNode;
}

/**
 * Bootstrap Provider
 *
 * 在应用启动时调用 Bootstrap API 获取区域配置，
 * 并将数据同步到 RegionStore 供全局使用。
 *
 * @example
 * ```tsx
 * <BootstrapProvider>
 *   <App />
 * </BootstrapProvider>
 * ```
 */
export function BootstrapProvider({ children }: BootstrapProviderProps) {
  // 获取当前语言作为 locale 参数
  const currentLocale = i18n.language;

  // 调用 Bootstrap API
  const { data, isLoading, error } = useBootstrap(currentLocale);

  // 获取 RegionStore 的 actions
  const setBootstrapData = useRegionStore((state) => state.setBootstrapData);
  const setLoading = useRegionStore((state) => state.setLoading);
  const setError = useRegionStore((state) => state.setError);

  // 同步 Bootstrap 数据到 RegionStore
  useEffect(() => {
    if (isLoading) {
      setLoading(true);
      return;
    }

    if (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Bootstrap API 调用失败";
      setError(errorMessage);
      console.error("Bootstrap API error:", error);
      return;
    }

    if (data) {
      setBootstrapData(data);
    }
  }, [data, isLoading, error, setBootstrapData, setLoading, setError]);

  // 直接渲染子组件，不阻塞渲染
  // 区域数据会在后台加载完成后自动更新到 Store
  return <>{children}</>;
}

export default BootstrapProvider;
